import { useEffect, useRef, useState } from 'react';
import { usePermissions } from '../contexts/PermissionContext';

/**
 * "Which of these actions may I do on this screen?"
 *
 *   const can = useCapabilities({
 *     edit: 'locations:update',
 *     sync: 'locations:sync',
 *   });
 *   {can.sync && <SyncButton />}
 *
 * Every admin screen needs this and writing it inline each time drifts. It also
 * has two traps worth getting right once:
 *
 *   - It must start DENIED. Starting allowed makes buttons flash on screen and
 *     vanish, and a slow lookup would leave them clickable.
 *   - The effect must not key on hasPermissions' identity alone. It is stable
 *     in production, but anything returning a fresh function per render turns
 *     that into a re-render loop — which is exactly what happened the first
 *     time this was written inline.
 */
export default function useCapabilities(spec) {
  // Outside a PermissionProvider (a screen rendered on its own, a unit test of a host component)
  // usePermissions throws. Fail closed instead: every capability stays denied, the screen stays up.
  let hasPermissions;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- called on every render; the try only catches the missing-provider throw
    ({ hasPermissions } = usePermissions());
  } catch {
    hasPermissions = undefined;
  }

  const [can, setCan] = useState(() =>
    Object.fromEntries(Object.keys(spec).map((key) => [key, false]))
  );

  // `spec` is an object literal at every call site and `hasPermissions` may
  // not be identity-stable (a mocked context, or a provider that re-creates
  // it), so the effect keys on NEITHER — it runs once per mount, reading the
  // latest function through a ref. Two traps this shape avoids:
  //
  //   - A once-only `useRef` guard breaks under React 18 StrictMode (dev
  //     server): mount → cleanup → mount keeps refs, so the guard is already
  //     set on the real mount, the hook never asks again, and the first answer
  //     lands in a cancelled closure. Every capability stayed false for the
  //     whole session — but only on `npm start`, never in the prod build.
  //   - Keying the effect on `hasPermissions` identity re-fires it on every
  //     render when that identity is unstable — a re-render loop.
  //
  // A screen whose permissions change mid-session gets them on remount, which
  // is when PermissionContext reloads them anyway.
  const hasPermissionsRef = useRef(hasPermissions);
  hasPermissionsRef.current = hasPermissions;

  useEffect(() => {
    const lookup = hasPermissionsRef.current;
    const entries = Object.entries(spec);
    let cancelled = false;

    // Fail closed rather than throw. A context that cannot answer must leave
    // every capability denied — a crashing hook would take the whole screen
    // down, which is a worse outcome than a missing button.
    if (typeof lookup !== 'function') return undefined;

    let pending;
    try {
      pending = lookup(
        entries.map(([, name]) => {
          const [resource, action] = name.split(':');
          return { resource, action };
        })
      );
    } catch {
      return undefined;      // same reasoning: deny, do not take the page down
    }

    const apply = (results) => {
      if (cancelled) return;
      setCan(
        Object.fromEntries(
          entries.map(([key, name]) => [key, (results || {})[name] === true])
        )
      );
    };

    // A caller that can answer synchronously gets applied synchronously. This
    // runs inside an effect, so the state lands before paint and the control
    // never flashes in. In production hasPermissions() returns a promise and
    // this takes the async path.
    if (pending && typeof pending.then === 'function') {
      // Fail closed: an unreachable permission API must not hand out buttons.
      pending.then(apply).catch(() => {});
    } else {
      apply(pending);
    }

    // StrictMode's discarded first mount is cancelled here; its remount runs
    // the effect again and that answer is the one that lands.
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return can;
}
